import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

type Props = {
  question: any;
  value: unknown;
  disabled?: boolean;
  scales?: any;
  answersOnly?: boolean;
  onChange: (value: unknown) => void;
};

const optionId = (o: any) => o?.id ?? o?.emoji ?? o?.label ?? String(o);
const optionLabel = (o: any) => o?.label ?? o?.emoji ?? o?.id ?? String(o);

/** True when the stored answer is an explicit skip marker `{ skipped: true }`. */
export const isSkippedAnswer = (raw: unknown): boolean =>
  raw != null &&
  typeof raw === "object" &&
  !Array.isArray(raw) &&
  (raw as Record<string, unknown>).skipped === true;

/** Unwrap Mongo answer shapes; skip skipped flags. */
export const normalizeAnswerValue = (raw: unknown): unknown => {
  if (raw == null) return null;
  if (typeof raw !== "object" || Array.isArray(raw)) return raw;
  const obj = raw as Record<string, unknown>;
  if (obj.skipped === true) return null;
  for (const key of ["value", "text", "answer", "id", "v", "label", "emoji"]) {
    if (obj[key] != null && obj[key] !== "") return obj[key];
  }
  return raw;
};

/** Safe string for subjective / free-text answers (never `[object Object]`). */
export const toDisplayString = (raw: unknown, depth = 0): string => {
  if (raw == null) return "";
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    return trimmed === "[object Object]" ? "" : trimmed;
  }
  if (typeof raw === "number" || typeof raw === "boolean") return String(raw);
  if (depth > 4) return "";
  if (Array.isArray(raw)) {
    return raw
      .map((item) => toDisplayString(item, depth + 1))
      .filter(Boolean)
      .join(", ");
  }
  if (typeof raw === "object") {
    const unwrapped = normalizeAnswerValue(raw);
    if (unwrapped !== raw) return toDisplayString(unwrapped, depth + 1);
    const obj = raw as Record<string, unknown>;
    for (const key of ["text", "value", "answer", "label", "content", "data", "notes"]) {
      if (obj[key] != null && obj[key] !== "") {
        const next = toDisplayString(obj[key], depth + 1);
        if (next) return next;
      }
    }
  }
  return "";
};

const optionMatches = (o: any, selected: unknown): boolean => {
  if (selected == null || selected === "") return false;
  const candidates = [o?.id, o?.emoji, o?.label]
    .filter((x) => x != null && x !== "")
    .map(String);
  const sel = String(selected);
  return candidates.includes(sel);
};

const optionInMulti = (o: any, selected: unknown[]): boolean =>
  selected.some((s) => optionMatches(o, s));

const CUSTOM = "Custom";

const DEFAULT_QUANTITY_OPTIONS = ["1", "2", "3", "4", "5", "6", CUSTOM];

const DEFAULT_UNIT_OPTIONS = [
  "tsp",
  "tbsp",
  "katori",
  "cup",
  "glass",
  "plate",
  "piece",
  "bowl",
  "ml",
  CUSTOM,
];

const DEFAULT_TYPE_OPTIONS = [
  "Cereal/Grain",
  "Protein (Dal/Egg/Meat)",
  "Vegetable",
  "Fruit",
  "Dairy",
  "Beverage",
  "Fried/Processed",
  "Sweet/Dessert",
  "Oil/Ghee/Fat",
  "Other",
];

type RecallItem = {
  name: string;
  quantity: string;
  unit?: string;
  type: string;
  customQuantity?: string;
  customUnit?: string;
  customType?: string;
};

type RecallSlot = {
  time?: string;
  item?: string;
  portion?: string;
  items?: RecallItem[];
};

const toTimeInputValue = (raw: unknown): string => {
  const s = String(raw || "").trim();
  if (!s) return "";
  const match = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!match) return "";
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return "";
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

const HOURS_12 = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const MINUTES_30 = [0, 30] as const;

type ClockValue = {
  hour12: number;
  minute: 0 | 30;
  period: "AM" | "PM";
};

const snapMinute = (minutes: number): 0 | 30 => (minutes < 15 ? 0 : 30);

const parseClock = (raw: unknown): ClockValue | null => {
  const hhmm = toTimeInputValue(raw);
  if (!hhmm) return null;
  let hours = Number(hhmm.slice(0, 2));
  let minutes = Number(hhmm.slice(3, 5));
  if (minutes >= 45) {
    hours = (hours + 1) % 24;
    minutes = 0;
  } else {
    minutes = snapMinute(minutes);
  }
  const period: "AM" | "PM" = hours >= 12 ? "PM" : "AM";
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  return { hour12, minute: minutes as 0 | 30, period };
};

const clockToHHmm = (clock: ClockValue): string => {
  let hours = clock.hour12 % 12;
  if (clock.period === "PM") hours += 12;
  return `${String(hours).padStart(2, "0")}:${clock.minute === 30 ? "30" : "00"}`;
};

const clockLabel = (clock: ClockValue): string =>
  `${clock.hour12}:${clock.minute === 30 ? "30" : "00"} ${clock.period}`;

const DEFAULT_CLOCK: ClockValue = { hour12: 8, minute: 0, period: "AM" };

const RecallTimePicker: React.FC<{
  value?: string;
  disabled?: boolean;
  onChange: (next: string) => void;
}> = ({ value, disabled, onChange }) => {
  const parsed = parseClock(value);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<ClockValue>(parsed || DEFAULT_CLOCK);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const wrapRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const hourRef = useRef<HTMLButtonElement>(null);

  const placePop = () => {
    const btn = btnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const width = 220;
    const height = 470;
    let left = rect.right - width;
    if (left < 12) left = 12;
    if (left + width > window.innerWidth - 12) {
      left = window.innerWidth - width - 12;
    }
    let top = rect.bottom + 8;
    if (top + height > window.innerHeight - 12) {
      top = Math.max(12, rect.top - height - 8);
    }
    setPos({ top, left });
  };

  useLayoutEffect(() => {
    if (!open) return;
    placePop();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setDraft(parseClock(value) || DEFAULT_CLOCK);
    const onDoc = (event: MouseEvent) => {
      const node = event.target as Node;
      if (wrapRef.current?.contains(node) || popRef.current?.contains(node)) {
        return;
      }
      setOpen(false);
    };
    const onReposition = () => placePop();
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [open, value]);

  useEffect(() => {
    if (!open || !hourRef.current) return;
    const col = hourRef.current.parentElement;
    if (!col) return;
    col.scrollTop =
      hourRef.current.offsetTop -
      col.clientHeight / 2 +
      hourRef.current.clientHeight / 2;
  }, [open, draft.hour12]);

  const commit = (next: ClockValue) => {
    setDraft(next);
    onChange(clockToHHmm(next));
  };

  const popup = open
    ? createPortal(
        <div
          ref={popRef}
          className="recall-clock-pop"
          role="dialog"
          aria-label="Choose time"
          style={{ top: pos.top, left: pos.left }}
        >
          <div className="recall-clock-col hours">
            {HOURS_12.map((hour) => (
              <button
                key={hour}
                type="button"
                ref={hour === draft.hour12 ? hourRef : undefined}
                className={`recall-clock-opt${
                  hour === draft.hour12 ? " sel" : ""
                }`}
                onClick={() => commit({ ...draft, hour12: hour })}
              >
                {String(hour).padStart(2, "0")}
              </button>
            ))}
          </div>
          <div className="recall-clock-col mins">
            {MINUTES_30.map((minute) => (
              <button
                key={minute}
                type="button"
                className={`recall-clock-opt${
                  minute === draft.minute ? " sel" : ""
                }`}
                onClick={() => commit({ ...draft, minute })}
              >
                {String(minute).padStart(2, "0")}
              </button>
            ))}
          </div>
          <div className="recall-clock-col period">
            {(["AM", "PM"] as const).map((period) => (
              <button
                key={period}
                type="button"
                className={`recall-clock-opt${
                  period === draft.period ? " sel" : ""
                }`}
                onClick={() => commit({ ...draft, period })}
              >
                {period}
              </button>
            ))}
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <div className={`recall-clock${open ? " is-open" : ""}`} ref={wrapRef}>
      <button
        ref={btnRef}
        type="button"
        className={`recall-clock-btn${parsed ? "" : " is-empty"}`}
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
      >
        {parsed ? clockLabel(parsed) : "Set time"}
      </button>
      {parsed && !disabled ? (
        <button
          type="button"
          className="recall-clock-clear"
          aria-label="Clear time"
          title="Clear time"
          onClick={() => {
            setOpen(false);
            onChange("");
          }}
        >
          ×
        </button>
      ) : null}
      {popup}
    </div>
  );
};

const comboOptions = (opts: string[]) =>
  opts.filter((option) => option && option !== CUSTOM);

const displayedCombo = (stored: string, custom?: string) =>
  stored === CUSTOM ? custom || "" : stored || "";

const RecallCombo: React.FC<{
  value: string;
  options: string[];
  placeholder?: string;
  disabled?: boolean;
  onChange: (next: string) => void;
}> = ({ value, options, placeholder, disabled, onChange }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const wrapRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const choices = comboOptions(options);
  const query = value.trim().toLowerCase();
  const filtered = query
    ? choices.filter((option) => option.toLowerCase().includes(query))
    : choices;
  const shown = filtered.length ? filtered : choices;

  const placeMenu = () => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPos({
      top: rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 88),
    });
  };

  useLayoutEffect(() => {
    if (open) placeMenu();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (event: MouseEvent) => {
      const node = event.target as Node;
      if (wrapRef.current?.contains(node) || menuRef.current?.contains(node)) {
        return;
      }
      setOpen(false);
    };
    const onMove = () => placeMenu();
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, true);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("resize", onMove);
      window.removeEventListener("scroll", onMove, true);
    };
  }, [open]);

  return (
    <div className={`recall-combo${open ? " is-open" : ""}`} ref={wrapRef}>
      <input
        className="inp recall-combo-input"
        disabled={disabled}
        placeholder={placeholder}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
      />
      {disabled ? null : (
        <button
          type="button"
          className="recall-combo-arrow"
          tabIndex={-1}
          aria-label="Show options"
          onMouseDown={(event) => {
            event.preventDefault();
            setOpen((v) => !v);
          }}
        />
      )}
      {open
        ? createPortal(
            <div
              ref={menuRef}
              className="recall-combo-menu"
              style={{ top: pos.top, left: pos.left, width: pos.width }}
            >
              {shown.map((option) => (
                <button
                  key={option}
                  type="button"
                  className={`recall-combo-opt${
                    option === value ? " sel" : ""
                  }`}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    onChange(option);
                    setOpen(false);
                  }}
                >
                  {option}
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
};

const blankRecallItem = (): RecallItem => ({
  name: "",
  quantity: "",
  unit: "",
  type: "",
  customQuantity: "",
  customUnit: "",
  customType: "",
});

const itemsFromSlot = (slot: RecallSlot | undefined): RecallItem[] => {
  if (!slot) return [];
  if (Array.isArray(slot.items)) return slot.items;
  if (slot.item || slot.portion) {
    return [
      {
        name: slot.item || "",
        quantity: slot.portion || "",
        type: "",
        customQuantity: "",
        customType: "",
      },
    ];
  }
  return [];
};

const UNIT_ALIASES: Record<string, string> = {
  pieces: "piece",
  pcs: "piece",
  pc: "piece",
  katoris: "katori",
  cups: "cup",
  glasses: "glass",
  plates: "plate",
  bowls: "bowl",
  tsps: "tsp",
  tbsps: "tbsp",
};

const splitLegacyPortion = (raw: string): { qty: string; unit: string } => {
  const s = String(raw || "").trim();
  const match = s.match(/^(\d+(?:\.\d+)?)\s+(.+)$/);
  if (!match) return { qty: s, unit: "" };
  const unitRaw = match[2].trim();
  const aliased = UNIT_ALIASES[unitRaw.toLowerCase()];
  return { qty: match[1], unit: aliased || unitRaw };
};

const itemQtyUnit = (item: RecallItem) => {
  if (item.unit) {
    return { qty: item.quantity || "", unit: item.unit };
  }
  return splitLegacyPortion(item.quantity || "");
};

const RECALL_SLOT_ORDER = [
  "wake_up",
  "breakfast",
  "mid_morning",
  "lunch",
  "evening_snack",
  "dinner",
  "bedtime",
];

const recallSlotLabel = (slot: string) =>
  slot.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const isRecallSlot = (value: unknown): value is RecallSlot => {
  if (value == null || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  const slot = value as RecallSlot;
  return (
    Array.isArray(slot.items) ||
    slot.item != null ||
    slot.portion != null ||
    slot.time != null
  );
};

/** True when the stored answer is a 24-hour meal-grid object, not free text. */
export const isRecallAnswer = (raw: unknown): raw is Record<string, RecallSlot> => {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) return false;
  const obj = raw as Record<string, unknown>;
  if (obj.skipped === true) return false;
  return Object.entries(obj).some(
    ([key, value]) => key !== "skipped" && isRecallSlot(value),
  );
};

const formatRecallItemLine = (item: RecallItem): string => {
  const split = itemQtyUnit(item);
  const quantity = displayedCombo(split.qty, item.customQuantity);
  const unit = displayedCombo(split.unit, item.customUnit);
  const type = displayedCombo(item.type, item.customType);
  const name = String(item.name || "").trim();
  const amount = [quantity, unit].filter(Boolean).join(" ");
  const extras = [amount, type].filter(Boolean).join(", ");
  if (!name && !extras) return "";
  if (!name) return extras;
  return extras ? `${name} (${extras})` : name;
};

const formatRecallSlotTime = (raw?: string): string => {
  const parsed = parseClock(raw);
  if (parsed) return clockLabel(parsed);
  return String(raw || "").trim();
};

/** Meal-grid answer → readable lines for the free-text diary. Does not mutate storage. */
export const formatRecallAnswerAsText = (raw: Record<string, RecallSlot>): string => {
  const seen = new Set<string>();
  const keys = RECALL_SLOT_ORDER.concat(Object.keys(raw));
  const lines: string[] = [];
  keys.forEach((slot) => {
    if (seen.has(slot) || slot === "skipped" || !isRecallSlot(raw[slot])) return;
    seen.add(slot);
    const slotData = raw[slot];
    const foods = itemsFromSlot(slotData)
      .map(formatRecallItemLine)
      .filter(Boolean)
      .join(", ");
    const time = formatRecallSlotTime(slotData.time);
    if (!time && !foods) return;
    const body = time && foods ? `${time} · ${foods}` : time || foods;
    lines.push(`${recallSlotLabel(slot)}: ${body}`);
  });
  return lines.join("\n");
};

const QuestionField: React.FC<Props> = ({
  question,
  value,
  disabled,
  scales,
  answersOnly,
  onChange,
}) => {
  const type = question?.type;
  const options = question?.options || [];
  const multi = question?.multi === true;
  const normalized = normalizeAnswerValue(value);
  const skipped = isSkippedAnswer(value);

  const wrap = (node: React.ReactNode) =>
    skipped && disabled ? (
      <div className="is-skipped-field" aria-label="Skipped">
        {node}
      </div>
    ) : (
      <>{node}</>
    );

  if (type === "subjective") {
    const liveValue =
      typeof normalized === "string"
        ? normalized === "[object Object]"
          ? ""
          : normalized
        : isRecallAnswer(normalized)
          ? formatRecallAnswerAsText(normalized)
          : toDisplayString(normalized);
    // A meal-grid object shown in this free-text field must stay read-only.
    // onChange emits a string, and saving that would replace the structured recall.
    const recallLocked = isRecallAnswer(normalized);
    return wrap(
      <textarea
        className="inp"
        disabled={disabled || recallLocked}
        value={liveValue}
        placeholder={question.helper || question.help || ""}
        onChange={(e) => {
          if (recallLocked) return;
          onChange(e.target.value);
        }}
      />,
    );
  }

  if (type === "toggle") {
    const opts = [
      { id: "yes", label: question.yes?.label || "Yes" },
      { id: "no", label: question.no?.label || "No" },
    ];
    return wrap(
      <div className="chips">
        {(answersOnly
          ? opts.filter((o) => String(normalized) === o.id)
          : opts
        ).map((o) => (
          <button
            key={o.id}
            type="button"
            className={`chip ${String(normalized) === o.id ? "sel" : ""}`}
            disabled={disabled}
            onClick={() => onChange(o.id)}
          >
            {o.label}
          </button>
        ))}
      </div>,
    );
  }

  if (type === "chips" || type === "cards" || type === "emoji") {
    const selectedList = multi
      ? Array.isArray(normalized)
        ? (normalized as unknown[])
        : normalized != null
          ? [normalized]
          : []
      : null;

    const visibleOptions = answersOnly
      ? options.filter((o: any) =>
          multi
            ? optionInMulti(o, selectedList || [])
            : optionMatches(o, normalized),
        )
      : options;

    if (type === "cards" || type === "emoji" || (type === "chips" && !multi)) {
      return wrap(
        <div className={type === "emoji" ? "chips" : "rlist"}>
          {visibleOptions.map((o: any) => {
            const id = optionId(o);
            const sel = optionMatches(o, normalized);
            if (type === "emoji") {
              return (
                <button
                  key={id}
                  type="button"
                  className={`chip ${sel ? "sel" : ""}`}
                  disabled={disabled}
                  onClick={() => onChange(id)}
                >
                  {o.emoji ? `${o.emoji} ` : ""}
                  {optionLabel(o)}
                </button>
              );
            }
            return (
              <button
                key={id}
                type="button"
                className={`ritem ${sel ? "sel" : ""}`}
                disabled={disabled}
                onClick={() => onChange(id)}
              >
                <span className="radio-dot" aria-hidden>
                  <span className="radio-dot-inner" />
                </span>
                <span>
                  {o.emoji ? `${o.emoji} ` : ""}
                  {optionLabel(o)}
                </span>
              </button>
            );
          })}
        </div>,
      );
    }

    return wrap(
      <div className="chips" data-multi="1">
        {visibleOptions.map((o: any) => {
          const id = optionId(o);
          const sel = optionInMulti(o, selectedList || []);
          return (
            <button
              key={id}
              type="button"
              className={`chip ${sel ? "sel" : ""}`}
              disabled={disabled}
              onClick={() => {
                const cur = [...(selectedList || [])].map(String);
                if (sel) onChange(cur.filter((x) => x !== String(id)));
                else onChange([...cur, String(id)]);
              }}
            >
              <svg
                className="ck"
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ marginRight: 5, verticalAlign: -1 }}
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
              {optionLabel(o)}
            </button>
          );
        })}
      </div>,
    );
  }

  if (type === "likert") {
    const scale = scales?.default || [1, 2, 3, 4, 5];
    const items: { v: number; short?: string }[] = Array.isArray(scale)
      ? scale.map((s: any) =>
          typeof s === "object"
            ? { v: Number(s.v), short: s.short }
            : { v: Number(s) },
        )
      : [1, 2, 3, 4, 5].map((v) => ({ v }));
    const numVal = Number(normalized);
    const legendItems = items.filter((s) => s.short);
    return wrap(
      <div className="likert-wrap">
        <div className="likert">
          {items.map((s) => (
            <button
              key={s.v}
              type="button"
              className={`lk ${numVal === s.v ? "sel" : ""}`}
              disabled={disabled}
              title={s.short || undefined}
              aria-label={s.short ? `${s.v}: ${s.short}` : String(s.v)}
              onClick={() => onChange(s.v)}
            >
              {s.v}
            </button>
          ))}
        </div>
        {legendItems.length > 0 ? (
          <div className="likert-legend" aria-hidden={false}>
            {legendItems.map((s) => (
              <span className="ll-item" key={s.v}>
                <strong>{s.v}</strong>
                {s.short}
              </span>
            ))}
          </div>
        ) : null}
      </div>,
    );
  }

  if (type === "slider" || type === "stepper" || type === "scale") {
    const min = Number(question.min ?? 0);
    const max = Number(question.max ?? 10);
    const step = Number(question.step ?? 1) || 1;
    const span = max - min;
    const numVal =
      normalized == null || normalized === ""
        ? ""
        : Number(normalized);

    // Wide ranges (weight, reps, etc.) → compact number input (PRD num)
    if (span > 11) {
      return wrap(
        <div className="inp-wrap num-wrap">
          <input
            className={`inp num ${question.unit ? "has-unit" : ""}`}
            type="number"
            inputMode="decimal"
            disabled={disabled}
            min={min}
            max={max}
            step={step}
            value={numVal === "" || Number.isNaN(numVal) ? "" : numVal}
            placeholder={question.help || question.helper || `${min}–${max}`}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "") {
                onChange(null);
                return;
              }
              const n = Number(raw);
              if (!Number.isNaN(n)) onChange(n);
            }}
          />
          {question.unit ? <span className="unit">{question.unit}</span> : null}
        </div>,
      );
    }

    const btns: number[] = [];
    for (let i = min; i <= max; i += step) btns.push(i);
    return wrap(
      <div className="scale ns">
        {btns.map((n) => (
          <button
            key={n}
            type="button"
            className={`sbtn ${Number(numVal) === n ? "sel" : ""}`}
            disabled={disabled}
            onClick={() => onChange(n)}
          >
            {n}
          </button>
        ))}
        {question.unit && <span className="scale-ends">{question.unit}</span>}
      </div>,
    );
  }

  if (type === "recall") {
    const slots: string[] = question.slots || [];
    const map = (
      normalized && typeof normalized === "object" && !Array.isArray(normalized)
        ? normalized
        : {}
    ) as Record<string, RecallSlot>;
    const quantityOptions: string[] =
      question.itemFields?.quantityOptions || DEFAULT_QUANTITY_OPTIONS;
    const unitOptions: string[] =
      question.itemFields?.unitOptions || DEFAULT_UNIT_OPTIONS;
    const typeOptions: string[] =
      question.itemFields?.typeOptions || DEFAULT_TYPE_OPTIONS;

    const patchSlot = (slot: string, nextSlot: RecallSlot) => {
      onChange({ ...map, [slot]: nextSlot });
    };

    return wrap(
      <div className="recall-stack">
        {slots.map((slot) => {
          const slotData = map[slot] || {};
          const items = itemsFromSlot(slotData);
          return (
            <div
              className={`recall-meal${items.length ? " has-items" : ""}`}
              key={slot}
            >
              <div className="recall-meal-head">
                <div className="recall-meal-title">
                  <span className="recall-slot">{recallSlotLabel(slot)}</span>
                  {items.length > 0 ? (
                    <span className="recall-count">
                      {items.length} {items.length === 1 ? "item" : "items"}
                    </span>
                  ) : null}
                </div>
                <label className="recall-time-wrap">
                  <span className="recall-time-label">Time</span>
                  <RecallTimePicker
                    value={slotData.time}
                    disabled={disabled}
                    onChange={(time) => patchSlot(slot, { ...slotData, time })}
                  />
                </label>
              </div>
              {items.length > 0 ? (
                <div className="recall-items">
                  {items.map((item, idx) => {
                    const split = itemQtyUnit(item);
                    const qtyValue = displayedCombo(
                      split.qty,
                      item.customQuantity,
                    );
                    const unitValue = displayedCombo(
                      split.unit,
                      item.customUnit,
                    );
                    const typeValue = displayedCombo(
                      item.type,
                      item.customType,
                    );
                    const updateItem = (patch: Partial<RecallItem>) => {
                      const nextItems = items.map((row, i) =>
                        i === idx ? { ...row, ...patch } : row,
                      );
                      patchSlot(slot, { time: slotData.time, items: nextItems });
                    };
                    return (
                      <div
                        className={`recall-item${disabled ? " is-readonly" : ""}`}
                        key={`${slot}-${idx}`}
                      >
                        <input
                          className="inp recall-food"
                          disabled={disabled}
                          placeholder="Food item"
                          value={item.name || ""}
                          onChange={(e) => updateItem({ name: e.target.value })}
                        />
                        <RecallCombo
                          value={qtyValue}
                          options={quantityOptions}
                          placeholder="Qty"
                          disabled={disabled}
                          onChange={(quantity) =>
                            updateItem({
                              quantity,
                              unit: unitValue,
                              customQuantity: "",
                            })
                          }
                        />
                        <RecallCombo
                          value={unitValue}
                          options={unitOptions}
                          placeholder="Unit"
                          disabled={disabled}
                          onChange={(unit) =>
                            updateItem({
                              quantity: qtyValue,
                              unit,
                              customUnit: "",
                            })
                          }
                        />
                        <RecallCombo
                          value={typeValue}
                          options={typeOptions}
                          placeholder="Type"
                          disabled={disabled}
                          onChange={(typeValueNext) =>
                            updateItem({
                              type: typeValueNext,
                              customType: "",
                            })
                          }
                        />
                        {!disabled ? (
                          <button
                            type="button"
                            className="recall-remove"
                            aria-label="Remove item"
                            onClick={() => {
                              const nextItems = items.filter((_, i) => i !== idx);
                              patchSlot(slot, {
                                time: slotData.time,
                                items: nextItems,
                              });
                            }}
                          >
                            ×
                          </button>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              ) : disabled ? (
                <div className="recall-empty">No food added</div>
              ) : null}
              {!disabled ? (
                <button
                  type="button"
                  className="recall-add"
                  onClick={() =>
                    patchSlot(slot, {
                      time: slotData.time,
                      items: [...items, blankRecallItem()],
                    })
                  }
                >
                  + Add food
                </button>
              ) : null}
            </div>
          );
        })}
      </div>,
    );
  }

  // Fallback text
  return wrap(
    <input
      className="inp"
      disabled={disabled}
      value={toDisplayString(normalized)}
      onChange={(e) => onChange(e.target.value)}
    />,
  );
};

export default QuestionField;
